import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Plus, X, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';

type Court = { id?: string; name: string; is_active: boolean; sort_order: number; notes?: string | null };
type Session = { id?: string; session_date: string; start_time: string; end_time: string; label?: string | null; is_active: boolean; sort_order: number };
type FormatCfg = {
  format: 'pool_bracket' | 'round_robin' | 'showcase';
  games_per_team_target: number;
  pool_size_target: number;
  bracket_format?: string | null;
  notes?: string | null;
  /** NCAA compliance: most games a team may play in one day. Null = no limit. */
  max_games_per_day: number | null;
  /** NCAA compliance: least minutes between a team's games. 0 = back-to-back allowed. */
  back_to_back_minutes: number;
  /** Read-only here (it is set on the Venues & courts timing tab); used as the default above. */
  buffer_minutes_between_games?: number | null;
};

export default function OperatorEventScheduleSetupPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [event, setEvent] = useState<any>(null);

  const [courts, setCourts] = useState<Court[]>([]);

  const [sessions, setSessions] = useState<Session[]>([]);

  const [format, setFormat] = useState<FormatCfg>({
    format: 'pool_bracket',
    games_per_team_target: 3,
    pool_size_target: 4,
    // The rule Mike actually operates under, so a new event is compliant
    // before anyone touches this page.
    max_games_per_day: 2,
    back_to_back_minutes: 0,
  });

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const markDirty = () => { setDirty(true); setJustSaved(false); };

  const fetchSetup = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: fnError } = await invokeEdgeFunction(
        'rle-operator-event-schedule-setup',
        { body: { event_id: eventId } }
      );
      if (fnError || !res?.ok) {
        setError(res?.error?.message || fnError?.message || 'Could not load schedule setup.');
      } else {
        setEvent(res.data.event);
        setCourts((res.data.courts ?? []).filter((c: any) => c.is_active !== false));
        setSessions((res.data.sessions ?? []).filter((s: any) => s.is_active !== false));
        const cfg = res.data.format_config;
        const buffer = Number(cfg?.buffer_minutes_between_games ?? 10);
        if (cfg) setFormat({
          format: cfg.format,
          games_per_team_target: cfg.games_per_team_target,
          pool_size_target: cfg.pool_size_target,
          bracket_format: cfg.bracket_format,
          notes: cfg.notes,
          max_games_per_day: cfg.max_games_per_day ?? 2,
          back_to_back_minutes: Number(cfg.back_to_back_minutes ?? 0),
          buffer_minutes_between_games: cfg.buffer_minutes_between_games ?? null,
        });
        // A brand new event has no row yet: the warning starts on, at the
        // changeover gap, because the very next slot IS back to back.
        else setFormat(prev => ({
          ...prev,
          back_to_back_minutes: buffer,
          buffer_minutes_between_games: buffer,
        }));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchSetup(); /* eslint-disable-next-line */ }, [eventId]);

  // ---------- COURTS ----------
  const addCourt = () => { markDirty(); setCourts(prev => [...prev, { name: '', is_active: true, sort_order: prev.length, notes: null }]); };
  const updateCourt = (idx: number, patch: Partial<Court>) => {
    markDirty();
    setCourts(prev => prev.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };
  const removeCourt = (idx: number) => { markDirty(); setCourts(prev => prev.filter((_, i) => i !== idx)); };

  // ---------- SESSIONS ----------
  const addSession = () => {
    markDirty();
    const defaultDate = event?.start_date ?? new Date().toISOString().slice(0, 10);
    setSessions(prev => [...prev, { session_date: defaultDate, start_time: '09:00', end_time: '12:00', label: null, is_active: true, sort_order: prev.length }]);
  };
  const updateSession = (idx: number, patch: Partial<Session>) => {
    markDirty();
    setSessions(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };
  const removeSession = (idx: number) => { markDirty(); setSessions(prev => prev.filter((_, i) => i !== idx)); };

  // ---------- FORMAT ----------
  const updateFormat = (patch: Partial<FormatCfg>) => { markDirty(); setFormat(prev => ({ ...prev, ...patch })); };

  // ---------- SAVE ALL ----------
  const saveAll = async () => {
    setSaving(true);
    try {
      const calls = [
        { action: 'save_courts', event_id: eventId, courts },
        { action: 'save_sessions', event_id: eventId, sessions },
        { action: 'save_format', event_id: eventId, format },
      ];
      for (const body of calls) {
        const { data: res, error: fnError } = await invokeEdgeFunction(
          'rle-operator-event-schedule-setup',
          { body }
        );
        if (fnError || !res?.ok) {
          toast.error(res?.error?.message || fnError?.message || 'Failed to save setup');
          return;
        }
      }
      setDirty(false);
      setJustSaved(true);
      toast.success('Schedule setup saved.');
      await fetchSetup();
      setTimeout(() => setJustSaved(false), 3000);
    } finally { setSaving(false); }
  };


  // Build the date options for the session date dropdown from event start/end.
  const dateOptions: string[] = [];
  if (event?.start_date && event?.end_date) {
    const s = parseLocalDate(event.start_date + 'T12:00:00');
    const e = parseLocalDate(event.end_date + 'T12:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      dateOptions.push(d.toISOString().slice(0, 10));
    }
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
        <button
          onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
          className="flex items-center text-sm text-slate-600 hover:text-slate-900 mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Event overview
        </button>
        <h1 className="text-2xl font-bold text-slate-900">Schedule setup</h1>
        <p className="text-sm text-slate-600">
          {event?.name} — configure courts, sessions, and format before generating a schedule.
        </p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-sm text-amber-600">Unsaved changes</span>}
          {!dirty && justSaved && (
            <span className="flex items-center text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4 mr-1" /> Saved
            </span>
          )}
          <Button onClick={saveAll} disabled={saving || !dirty}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save setup
          </Button>
        </div>
      </div>

      {/* COURTS */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Courts / fields</h2>
              <p className="text-sm text-slate-600">Physical play locations available at this event.</p>
            </div>
            <Button variant="outline" size="sm" onClick={addCourt}>
              <Plus className="h-4 w-4 mr-1" /> Add court
            </Button>
          </div>
          {courts.length === 0 ? (
            <p className="text-sm text-slate-500">No courts yet. Click "Add court" to get started.</p>
          ) : (
            <div className="space-y-2">
              {courts.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={c.name}
                    placeholder="Court name (e.g. Court 1)"
                    onChange={e => updateCourt(idx, { name: e.target.value })}
                    className="flex-1"
                  />
                  <Input
                    value={c.notes ?? ''}
                    placeholder="Notes (optional)"
                    onChange={e => updateCourt(idx, { notes: e.target.value })}
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeCourt(idx)} title="Remove">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SESSIONS */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Time slots / sessions</h2>
              <p className="text-sm text-slate-600">Windows when games can be scheduled. Multiple per day allowed.</p>
            </div>
            <Button variant="outline" size="sm" onClick={addSession}>
              <Plus className="h-4 w-4 mr-1" /> Add session
            </Button>
          </div>
          {sessions.length === 0 ? (
            <p className="text-sm text-slate-500">No sessions yet. Click "Add session" to define a time window.</p>
          ) : (
            <div className="space-y-3">
              {sessions.map((s, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                  <div className="md:col-span-3">
                    <Label className="text-xs">Date</Label>
                    <Select value={s.session_date} onValueChange={v => updateSession(idx, { session_date: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select date" />
                      </SelectTrigger>
                      <SelectContent>
                        {dateOptions.map(d => (
                          <SelectItem key={d} value={d}>
                            {new Date(d + 'T12:00:00').toLocaleDateString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Start</Label>
                    <Input type="time" value={s.start_time} onChange={e => updateSession(idx, { start_time: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">End</Label>
                    <Input type="time" value={s.end_time} onChange={e => updateSession(idx, { end_time: e.target.value })} />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">Label (optional)</Label>
                    <Input value={s.label ?? ''} placeholder="e.g. Morning session" onChange={e => updateSession(idx, { label: e.target.value })} />
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    <Button variant="ghost" size="icon" onClick={() => removeSession(idx)} title="Remove">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* NCAA COMPLIANCE LIMITS */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-lg font-semibold text-slate-900">Games per day and rest</h2>
          <p className="text-sm text-slate-600 mb-4">
            These two numbers are what the NCAA compliance check reads. The games-per-day
            report, the publish check and the grid all measure against them. A division can
            override either one in its own advanced settings.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Most games a team plays in one day</Label>
              <Input
                type="number"
                min={0}
                value={format.max_games_per_day ?? ''}
                placeholder="No limit"
                onChange={e => {
                  const raw = e.target.value.trim();
                  const n = raw === '' ? null : parseInt(raw, 10);
                  updateFormat({ max_games_per_day: n == null || Number.isNaN(n) || n <= 0 ? null : n });
                }}
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave empty for no limit. NCAA events run at 2.
              </p>
            </div>
          </div>

          <div className="mt-4 w-full" data-testid="back-to-back-setting">
            <Label className="text-sm font-medium text-slate-900">Back-to-back warning</Label>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center">
              <span className="text-sm text-slate-700">
                Warn when a team's next game starts within
              </span>
              <Input
                type="number"
                min={0}
                className="w-full sm:w-24"
                value={format.back_to_back_minutes}
                onChange={e => updateFormat({ back_to_back_minutes: Math.max(0, parseInt(e.target.value || '0', 10) || 0) })}
              />
              <span className="text-sm text-slate-700">minutes of the last one finishing.</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Leave at 0 to turn the warning off. It is only ever a warning — you can still
              place games and publish with back-to-backs on the board.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* FORMAT */}
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-lg font-semibold text-slate-900">Format</h2>
          <p className="text-sm text-slate-600 mb-4">How games are structured and how many games each team plays.</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Tournament format</Label>
              <Select value={format.format} onValueChange={(v: any) => updateFormat({ format: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pool_bracket">Pool play + bracket</SelectItem>
                  <SelectItem value="round_robin">Round robin</SelectItem>
                  <SelectItem value="showcase">Showcase (no winner)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Games per team (target)</Label>
                <Input type="number" min={1} value={format.games_per_team_target} onChange={e => updateFormat({ games_per_team_target: parseInt(e.target.value || '1', 10) })} />
              </div>
              <div>
                <Label className="text-xs">Pool size (target)</Label>
                <Input type="number" min={2} value={format.pool_size_target} onChange={e => updateFormat({ pool_size_target: parseInt(e.target.value || '2', 10) })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={format.notes ?? ''}
                onChange={e => updateFormat({ notes: e.target.value })}
                placeholder="Any extra context for the scheduler (e.g. 'seed from prior tournament results')"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Ready to generate?</h2>
            <p className="text-sm text-muted-foreground max-w-xl">
              Once courts, sessions, and format are set, generate pools to create draft matchups. Teams are grouped by their registration level within each division.
            </p>
          </div>
          <Button onClick={() => navigate(`/admin-dashboard/events/${eventId}/schedule-draft`)}>
            Go to schedule draft
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
